import { useCallback, useReducer } from "react";
import type {
  FileUploadItem,
  ParsedExtraction,
  ExtractionResult,
  PdfTableRegionCollection,
} from "./types";

// =============================================================================
// Workflow state machine
// =============================================================================
// Mirrors Blazor's WorkflowStep enum: Upload, Processing, Review, Generating,
// Download. Generating is transient — no step circle of its own.

export type WorkflowStep =
  | "Upload"
  | "Processing"
  | "Review"
  | "Generating"
  | "Download";

export interface WorkflowState {
  step: WorkflowStep;
  files: FileUploadItem[];
  data: ParsedExtraction | null;
  originalData: ParsedExtraction | null; // shallow snapshot for correction-stat diffs (future)
  errorMessage: string | null;
  pdfUrl: string | null; // blob: URL for the generated PDF
  pdfFilename: string | null;
  completedSteps: Set<WorkflowStep>;
  sessionId: string | null;
  // Archive row for this run — created right after extraction combines,
  // updated (debounced) as the user edits, finalized at generate.
  documentId: string | null;
  progressMessages: ProgressMessage[];
}

export interface ProgressMessage {
  timestamp: string; // HH:mm:ss for parity with Blazor format
  fileName?: string;
  message: string;
}

export const initialWorkflowState: WorkflowState = {
  step: "Upload",
  files: [],
  data: null,
  originalData: null,
  errorMessage: null,
  pdfUrl: null,
  pdfFilename: null,
  completedSteps: new Set(),
  sessionId: null,
  documentId: null,
  progressMessages: [],
};

export type WorkflowAction =
  | { type: "ADD_FILES"; files: FileUploadItem[] }
  | { type: "REMOVE_FILE"; id: string }
  | { type: "SET_FILE_REGIONS"; id: string; regions: PdfTableRegionCollection | undefined }
  | { type: "SET_FILE_KIND"; id: string; kind: import("./types").FileKind }
  | { type: "SET_STEP"; step: WorkflowStep }
  | { type: "START_PROCESSING" }
  | { type: "FILE_PROCESSED"; id: string; extraction: ExtractionResult }
  | { type: "FILE_FAILED"; id: string; error: string }
  | { type: "PARSED"; parsedByFileId: Record<string, ParsedExtraction> }
  | { type: "COMBINED"; data: ParsedExtraction }
  | { type: "MERGE_NEW_OBJECTIONS"; objections: ParsedExtraction["objections"] }
  | { type: "UPDATE_DATA"; patch: Partial<ParsedExtraction> }
  | {
      type: "UPDATE_OBJECTION";
      id: string;
      patch: {
        objectionText?: string;
        resolved?: boolean;
        assignedTo?: import("./types").ObjectionAssignee | null;
      };
    }
  | {
      type: "ASSIGN_ALL_OBJECTIONS";
      assignedTo: import("./types").ObjectionAssignee;
    }
  | { type: "ADD_OBJECTION" }
  | { type: "REMOVE_OBJECTION"; id: string }
  | { type: "TOGGLE_RESOLVED_VISIBILITY" }
  | { type: "GENERATING" }
  | { type: "GENERATED"; url: string; filename: string }
  | { type: "GENERATE_FAILED"; error: string }
  | { type: "BACK_FROM_DOWNLOAD" }
  | { type: "SET_SESSION_ID"; sessionId: string | null }
  | { type: "SET_DOCUMENT_ID"; documentId: string | null }
  | { type: "APPEND_PROGRESS"; message: ProgressMessage }
  | { type: "CLEAR_PROGRESS" }
  | { type: "RESET" };

export function reducer(state: WorkflowState, action: WorkflowAction): WorkflowState {
  switch (action.type) {
    case "ADD_FILES": {
      const existing = new Set(
        state.files.map((f) => `${f.file.name}|${f.file.size}`),
      );
      const additions = action.files.filter(
        (f) => !existing.has(`${f.file.name}|${f.file.size}`),
      );
      return { ...state, files: [...state.files, ...additions] };
    }

    case "REMOVE_FILE":
      return {
        ...state,
        files: state.files.filter((f) => f.id !== action.id),
      };

    case "SET_FILE_KIND":
      return {
        ...state,
        files: state.files.map((f) =>
          f.id === action.id ? { ...f, kind: action.kind } : f,
        ),
      };

    case "SET_FILE_REGIONS": {
      // If regions actually changed AND the file was already extracted, drop
      // the stale extraction/parsed and mark it Pending so the user is forced
      // back through Process. Otherwise the "All files already processed"
      // state lies — the cached extraction predates the new regions.
      const target = state.files.find((f) => f.id === action.id);
      if (!target) return state;
      const prevJson = target.tableRegions
        ? JSON.stringify(target.tableRegions)
        : "";
      const nextJson = action.regions ? JSON.stringify(action.regions) : "";
      if (prevJson === nextJson) return state;
      const wasProcessed = target.status === "Completed";
      return {
        ...state,
        files: state.files.map((f) =>
          f.id === action.id
            ? {
                ...f,
                tableRegions: action.regions,
                ...(wasProcessed
                  ? {
                      status: "Pending" as const,
                      extraction: undefined,
                      parsed: undefined,
                      errorMessage: undefined,
                    }
                  : {}),
              }
            : f,
        ),
      };
    }

    case "SET_STEP": {
      const completed = new Set(state.completedSteps);
      // Forward navigation marks the prior step "completed" — Blazor parity.
      const order: WorkflowStep[] = [
        "Upload",
        "Processing",
        "Review",
        "Generating",
        "Download",
      ];
      const fromIdx = order.indexOf(state.step);
      const toIdx = order.indexOf(action.step);
      if (toIdx > fromIdx) completed.add(state.step);
      return { ...state, step: action.step, completedSteps: completed };
    }

    case "START_PROCESSING":
      return {
        ...state,
        step: "Processing",
        completedSteps: new Set([...state.completedSteps, "Upload"]),
        files: state.files.map((f) =>
          f.status === "Pending" ? { ...f, status: "Processing" } : f,
        ),
      };

    case "FILE_PROCESSED":
      return {
        ...state,
        files: state.files.map((f) =>
          f.id === action.id
            ? { ...f, status: "Completed", extraction: action.extraction }
            : f,
        ),
      };

    case "FILE_FAILED":
      return {
        ...state,
        files: state.files.map((f) =>
          f.id === action.id
            ? { ...f, status: "Failed", errorMessage: action.error }
            : f,
        ),
      };

    case "PARSED":
      return {
        ...state,
        files: state.files.map((f) =>
          action.parsedByFileId[f.id]
            ? { ...f, parsed: action.parsedByFileId[f.id] }
            : f,
        ),
      };

    case "COMBINED":
      return {
        ...state,
        data: action.data,
        originalData: structuredClone(action.data),
        step: "Review",
        completedSteps: new Set([
          ...state.completedSteps,
          "Upload",
          "Processing",
        ]),
      };

    case "MERGE_NEW_OBJECTIONS": {
      if (!state.data) return state;
      // Mirror Blazor's MergeNewData: only add objections whose ids aren't
      // already present. Property fields are NEVER overwritten. Also strip
      // soft wraps from incoming text so Add More Files matches the initial
      // Process flow's cleanup.
      const existingIds = new Set(state.data.objections.map((o) => o.id));
      const additions = action.objections
        .filter((o) => !existingIds.has(o.id))
        .map((o) => ({
          ...o,
          objectionText: normalizeObjectionText(o.objectionText),
          text: normalizeObjectionText(o.text),
        }));
      return {
        ...state,
        data: {
          ...state.data,
          objections: [...state.data.objections, ...additions],
        },
      };
    }

    case "UPDATE_DATA":
      if (!state.data) return state;
      return { ...state, data: { ...state.data, ...action.patch } };

    case "UPDATE_OBJECTION":
      if (!state.data) return state;
      return {
        ...state,
        data: {
          ...state.data,
          objections: state.data.objections.map((o) =>
            o.id === action.id ? { ...o, ...action.patch, text: action.patch.objectionText ?? o.text } : o,
          ),
        },
      };

    case "ASSIGN_ALL_OBJECTIONS":
      if (!state.data) return state;
      // Assignee only matters for active objections (resolved ones don't
      // export), so leave resolved rows untouched.
      return {
        ...state,
        data: {
          ...state.data,
          objections: state.data.objections.map((o) =>
            o.resolved ? o : { ...o, assignedTo: action.assignedTo },
          ),
        },
      };

    case "ADD_OBJECTION": {
      if (!state.data) return state;
      const newId = crypto.randomUUID();
      return {
        ...state,
        data: {
          ...state.data,
          objections: [
            ...state.data.objections,
            {
              id: newId,
              text: "",
              objectionText: "",
              resolved: false,
              createdAt: new Date().toISOString(),
            },
          ],
        },
      };
    }

    case "REMOVE_OBJECTION":
      if (!state.data) return state;
      // Mirror Blazor: cannot remove if only 1 objection remains
      if (state.data.objections.length <= 1) return state;
      return {
        ...state,
        data: {
          ...state.data,
          objections: state.data.objections.filter((o) => o.id !== action.id),
        },
      };

    case "TOGGLE_RESOLVED_VISIBILITY":
      return state; // (UI-local state; see ReviewStep)

    case "GENERATING":
      return {
        ...state,
        step: "Generating",
        completedSteps: new Set([
          ...state.completedSteps,
          "Upload",
          "Processing",
          "Review",
        ]),
      };

    case "GENERATED":
      return {
        ...state,
        step: "Download",
        pdfUrl: action.url,
        pdfFilename: action.filename,
        completedSteps: new Set([
          ...state.completedSteps,
          "Upload",
          "Processing",
          "Review",
          "Generating",
        ]),
      };

    case "GENERATE_FAILED":
      return {
        ...state,
        step: "Review",
        errorMessage: action.error,
      };

    case "BACK_FROM_DOWNLOAD": {
      // Drop the generated PDF and step back to Review so the user can edit
      // and re-generate. Clear "Review", "Generating", and "Download" from
      // the completed set so the step indicator reflects the new active step.
      if (state.pdfUrl) URL.revokeObjectURL(state.pdfUrl);
      const completed = new Set(state.completedSteps);
      completed.delete("Review");
      completed.delete("Generating");
      completed.delete("Download");
      return {
        ...state,
        step: "Review",
        pdfUrl: null,
        pdfFilename: null,
        completedSteps: completed,
      };
    }

    case "SET_SESSION_ID":
      return { ...state, sessionId: action.sessionId };

    case "SET_DOCUMENT_ID":
      return { ...state, documentId: action.documentId };

    case "APPEND_PROGRESS":
      return {
        ...state,
        // Cap at 500 entries to keep state lean during long runs.
        progressMessages: [...state.progressMessages, action.message].slice(-500),
      };

    case "CLEAR_PROGRESS":
      return { ...state, progressMessages: [] };

    case "RESET":
      // Revoke the previous blob: URL to avoid leaks.
      if (state.pdfUrl) URL.revokeObjectURL(state.pdfUrl);
      return initialWorkflowState;

    default:
      return state;
  }
}

export function useWorkflow() {
  const [state, dispatch] = useReducer(reducer, initialWorkflowState);

  const reset = useCallback(() => dispatch({ type: "RESET" }), []);

  return { state, dispatch, reset };
}

// PDF text extraction preserves soft column wraps as literal "\n" characters,
// which makes short objections look like multi-line text in the editor. This
// collapses those soft wraps to spaces while preserving real paragraph
// breaks ("\n\n" or more) and hyphenated word splits across lines.
export function normalizeObjectionText(text: string): string {
  if (!text) return text;
  return (
    text
      // Hyphenated wrap: "cert-\nification" → "certification"
      .replace(/-\n(?=[a-z])/g, "")
      // Single newline that's NOT part of a paragraph break: join with space.
      // A "real" paragraph break tends to be \n\n; soft column wraps are
      // single \n inside a sentence.
      .replace(/(?<!\n)\n(?!\n)/g, " ")
      // Multiple spaces from the joins → one space
      .replace(/[ \t]{2,}/g, " ")
      .trim()
  );
}

// =============================================================================
// Combine helper — picks property info from the first successful file with a
// non-empty address; appends objections from all files in order. Mirrors
// Blazor's CombineAllParsedData(). Returns null if nothing succeeded.
// =============================================================================
export function combineParsed(
  files: FileUploadItem[],
): ParsedExtraction | null {
  const completed = files.filter((f) => f.status === "Completed" && f.parsed);
  if (completed.length === 0) return null;

  const propertySource =
    completed.find((f) => !!f.parsed?.address) ?? completed[0];
  const p = propertySource.parsed!;

  const objections = completed.flatMap((f) =>
    (f.parsed?.objections ?? []).map((o) => ({
      ...o,
      objectionText: normalizeObjectionText(o.objectionText),
      text: normalizeObjectionText(o.text),
      sourceFileName: o.sourceFileName ?? f.file.name,
    })),
  );

  return {
    address: p.address,
    houseNumber: p.houseNumber,
    streetName: p.streetName,
    borough: p.borough,
    block: p.block,
    lot: p.lot,
    bin: p.bin,
    cbNumber: p.cbNumber,
    applicantName: p.applicantName,
    jobNumber: p.jobNumber,
    addressCandidates: p.addressCandidates,
    lotCandidates: p.lotCandidates,
    objections,
    debugCondensedContext: p.debugCondensedContext,
    debugObjectionsSource: p.debugObjectionsSource,
    debugInfo: p.debugInfo,
  };
}
